import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiSearch, FiInfo, FiLock } from 'react-icons/fi';
import { XtreamCategory } from '../../api/xtreamAPI';
import { getCachedVodStreams } from '../../utils/vodStreamsCache';
import { getCachedSeriesStreams } from '../../utils/seriesStreamsCache';
import { getCachedMovieCategories, getCachedSeriesCategories } from '../../utils/xtreamCategoriesCache';
import { getRuntimeTmdbLanguage, getRuntimeTmdbRegion } from '../../api/tmdbAPI';
import { getGlobalSearch, getMatchedList } from '../../api/backendAPI';
import { consumeCatalogForceRefresh } from '../../utils/catalogRefresh';
import { useAuth } from '../../contexts/AuthContext';
import { useDevice } from '../../contexts/DeviceContext';
import { useFavoritesContext } from '../../contexts/FavoritesContext';
import { useParentalControls } from '../../contexts/ParentalControlsContext';
import { MovieDetailsEntry } from '../MovieDetails/MovieDetails';
import { SeriesDetailsEntry } from '../SeriesDetails/SeriesDetails';
import CuratedHintModal from '../../components/CuratedHintModal/CuratedHintModal';
import spinnerGif from '../../assets/spinner-tv.gif';
import PasswordModal, { PasswordModalMode } from '../../components/PasswordModal/PasswordModal';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import { useParentalContentLocks, ParentalContentType } from '../../hooks/useParentalContentLocks';
import './ContentList.css';

// ─── Types ─────────────────────────────────────────────────────────────────────

/**
 * source controls where grid data comes from:
 *  'xtream'      — browse Xtream streams directly; sidebar shows categories
 *  'now-playing'    — TMDB /movie/now_playing matched to Xtream; no sidebar
 *  'on-the-air'     — TMDB /tv/on_the_air matched to Xtream; no sidebar
 *  'recent-movies'  — TMDB /discover/movie (last 12 months) matched to Xtream; no sidebar
 *  'kids-movies'    — TMDB Animation/Family movies; no sidebar
 *  'kids-series'    — TMDB Kids genre TV series; no sidebar
 */
export type ContentListSource = 'xtream' | 'now-playing' | 'on-the-air' | 'recent-movies' | 'national-series' | 'top-movies' | 'top-series' | 'kids-movies' | 'kids-series' | 'anime-series' | 'anime-movies' | 'search' | 'favorites';

export interface ContentListEntry {
  type: 'movies' | 'series';
  source?: ContentListSource;
  title: string;
  categoryId?: string;
  categoryName?: string;
  /** Unique id per intentional navigation — changing it resets all internal UI state. */
  instanceId?: number;
}

interface ContentListProps {
  entry: ContentListEntry;
  isActive?: boolean;
  onBack?: () => void;
  onOpenMovieDetails?: (entry: MovieDetailsEntry) => void;
  onOpenSeriesDetails?: (entry: SeriesDetailsEntry) => void;
  onOpenLiveChannel?: (ch: import('../../api/xtreamAPI').XtreamContent, all: import('../../api/xtreamAPI').XtreamContent[]) => void;
}

// ─── Unified card type ──────────────────────────────────────────────────────────

interface ListCard {
  id: string;
  name: string;
  posterUrl?: string;
  streamId?: string;
  tmdbId?: number;
  backdropUrl?: string;
  /** Xtream category_id — set for national-series cards to enable sidebar filtering */
  categoryId?: string;
  /** Stream type — set for search results */
  streamType?: 'movie' | 'series' | 'live';
  /** Raw Xtream item — for live channel dispatch */
  liveData?: import('../../api/xtreamAPI').XtreamContent;
  /** Sinopse da listagem Xtream — para desambiguar busca TMDB */
  plot?: string;
  /** Ano de estreia — para desambiguar busca TMDB */
  year?: string;
  /** Data de estreia (releaseDate) — para desambiguar busca TMDB */
  releaseDate?: string;
  /** True when item came from Kids context */
  isKids?: boolean;
}

// ─── List result cache (24 h) ──────────────────────────────────────────────────

const LIST_CACHE_TTL = 24 * 60 * 60 * 1000;

function loadListCache(key: string): ListCard[] | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw) as { data: ListCard[]; ts: number };
    if (Date.now() - ts > LIST_CACHE_TTL) return null;
    return data;
  } catch { return null; }
}

function saveListCache(key: string, data: ListCard[]): ListCard[] {
  const seen = new Set<string>();
  const deduped = data.filter(c => { if (seen.has(c.id)) return false; seen.add(c.id); return true; });
  try { localStorage.setItem(key, JSON.stringify({ data: deduped, ts: Date.now() })); } catch {}
  return deduped;
}

// ─── Layout constants ───────────────────────────────────────────────────────────
//   Left sidebar: 215px
//   Right content area: 1280 - 215 = 1065px, padding 24px each side → 1017px
//   Grid: 5 cols × (1017 - 4×12) / 5 = 193.8 ≈ 194px wide per card
//   Card height (2:3): 194 × 1.5 = 291px
//   Row height with gap: 291 + 12 = 303px
//   Breadcrumb area: padding-top 28px + ~28px text + 16px gap = 72px
//   Grid viewport height: 720 - 72 = 648px  → 2 visible rows (2×291 + 12 = 594 ≤ 648)

const FAVORITES_CAT_ID = '__favorites__';
const BLOCK_CATEGORIES_CAT_ID = '__block_categories__';

const CARD_GAP_PX  = 12;
const LABEL_GAP_PX = 0;   // label is now inside the card
const LABEL_H_PX   = 0;   // label is now inside the card
const ROWS_VISIBLE = 2;

const CAT_ITEM_H_PX = 44;
const SEARCH_H_PX = 56;
const SEPARATOR_H_PX = 17; // margin-bottom on separator element (16px) + 1px line
// available height for category viewport:
const CATS_AREA_H_PX = 720 - SEARCH_H_PX - SEPARATOR_H_PX; // 647
const CATS_VISIBLE = Math.floor(CATS_AREA_H_PX / CAT_ITEM_H_PX); // 14

// ─── Shimmer helpers ────────────────────────────────────────────────────────────

const SHIMMER_CAT_WIDTHS = [80, 110, 65, 95, 120, 75, 100, 88, 115, 70, 90, 105, 60, 130];

const ShimmerCategories: React.FC = () => (
  <>
    {SHIMMER_CAT_WIDTHS.slice(0, 12).map((w, i) => (
      <div key={i} className="content-list__cat-shimmer">
        <div className="content-list__cat-shimmer-bar" style={{ width: w }} />
      </div>
    ))}
  </>
);

// ─── Component ─────────────────────────────────────────────────────────────────

export const ContentList: React.FC<ContentListProps> = ({
  entry,
  isActive = true,
  onBack,
  onOpenMovieDetails,
  onOpenSeriesDetails,
  onOpenLiveChannel,
}) => {
  const { authData } = useAuth();
  const { deviceId, activationPassword } = useDevice();
  const { getFavoritesByType, refreshFromServer: refreshFavorites } = useFavoritesContext();

  // Stale-while-revalidate: when opening favorites, re-fetch from server in background
  useEffect(() => {
    if (entry.source === 'favorites') refreshFavorites();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { t, i18n } = useTranslation();
  const {
    isKidsMode,
    hasPassword,
    verifyPassword,
    setPassword,
  } = useParentalControls();
  const {
    isEnabled: isParentalEnabled,
    isCategoryBlocked,
    isContentRestricted,
    setBlockedCategories,
  } = useParentalContentLocks();
  const parentalType: ParentalContentType = entry.type;

  const FAVORITES_CAT: XtreamCategory = useMemo(() => ({
    category_id: FAVORITES_CAT_ID,
    category_name: t('common.favorites'),
  }), [t]);

  const source: ContentListSource = entry.source ?? 'xtream';
  const isTmdbSource   = source !== 'xtream' && source !== 'search';
  // national-series / top-movies / top-series / search have a categories sidebar
  const hasSidebar  = source === 'xtream' || source === 'national-series' || source === 'top-movies' || source === 'top-series' || source === 'search' || source === 'favorites';
  // kids sources: no sidebar, wide 6-col grid (same as now-playing / recent-movies)
  const isWideGrid  = !hasSidebar;
  // sidebar sources that also show a filter bar in the topbar
  const hasFilterBar = isWideGrid || source === 'national-series' || source === 'top-movies' || source === 'top-series';
  const isCuratedSource = source === 'kids-movies' || source === 'kids-series' || source === 'anime-movies' || source === 'anime-series';

  // Grid layout — wide TMDB (no sidebar): 6 cols, 16px col-gap → card 192px → h 288px (2:3)
  //               Xtream / national-series (sidebar):  5 cols, 12px gap → card 194px → h 291px (2:3)
  const COLS      = isWideGrid ? 6 : 5;
  const CARD_H_PX = isWideGrid ? 288 : 291;
  const CELL_H_PX = CARD_H_PX + LABEL_GAP_PX + LABEL_H_PX;
  const ROW_H_PX  = CELL_H_PX + CARD_GAP_PX;

  // Sidebar — Xtream categories (xtream source) or Xtream series categories (national-series)
  const [categories, setCategories] = useState<XtreamCategory[]>([]);
  const [loadingCats, setLoadingCats] = useState(hasSidebar);
  const [selectedCatId, setSelectedCatId] = useState<string | undefined>(entry.categoryId);
  const todosLabel = source === 'top-movies'
    ? t('contentList.topMoviesAll')
    : source === 'top-series'
      ? t('contentList.topSeriesAll')
      : t('common.all');
  const [selectedCatName, setSelectedCatName] = useState(entry.categoryName ?? todosLabel);

  // Grid — unified ListCard
  const [content, setContent] = useState<ListCard[]>([]);
  const [loadingContent, setLoadingContent] = useState(true);
  const [matchedLoadingPhase, setMatchedLoadingPhase] = useState(0);
  const isMatchedListLoading = loadingContent && source !== 'xtream' && source !== 'search' && source !== 'favorites';

  // Search — query string and type filter (only used when source === 'search')
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTypeFilter, setSearchTypeFilter] = useState<'all' | 'movie' | 'series' | 'live'>('all');
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Wide grid search (no-sidebar TMDB sources)
  const [wideSearchQuery, setWideSearchQuery] = useState('');
  const wideSearchInputRef = useRef<HTMLInputElement>(null);

  // Curated hint modal
  const [curatedModalOpen, setCuratedModalOpen] = useState(false);
  const [categoryBlockMode, setCategoryBlockMode] = useState(false);
  const [draftBlockedCategoryIds, setDraftBlockedCategoryIds] = useState<Set<string>>(new Set());
  const [showCategorySaveModal, setShowCategorySaveModal] = useState(false);
  const [parentalPasswordMode, setParentalPasswordMode] = useState<PasswordModalMode | null>(null);
  const [parentalPasswordError, setParentalPasswordError] = useState('');

  // Focus — 'sidebar' controls the left panel, 'grid' controls the card grid
  type FocusSection = 'sidebar' | 'grid' | 'wide-search' | 'curated-hint';
  type SidebarFocus = 'search' | 'clear' | number; // number = index into [null, ...categories]

  const [focusSection, setFocusSection] = useState<FocusSection>(source === 'search' ? 'sidebar' : 'grid');
  const [sidebarFocus, setSidebarFocus] = useState<SidebarFocus>(source === 'search' ? 'search' : 0); // 'search' when source=search, else 0="Todos"
  const [focusedCard, setFocusedCard] = useState(0);

  // Scroll offsets
  const [catScrollIdx, setCatScrollIdx] = useState(0);  // first visible item in allCats
  const [gridScrollRow, setGridScrollRow] = useState(0);

  // Reset all UI state when instanceId changes (new intentional navigation, not back-from-details)
  const prevInstanceIdRef = React.useRef<number | undefined>(entry.instanceId);
  useEffect(() => {
    if (entry.instanceId === prevInstanceIdRef.current) return;
    prevInstanceIdRef.current = entry.instanceId;
    const newSource = entry.source ?? 'xtream';
    const newTodosLabel = newSource === 'top-movies'
      ? t('contentList.topMoviesAll')
      : newSource === 'top-series'
        ? t('contentList.topSeriesAll')
        : t('common.all');
    setSearchQuery('');
    setWideSearchQuery('');
    setSearchTypeFilter('all');
    setSelectedCatId(entry.categoryId);
    setSelectedCatName(entry.categoryName ?? newTodosLabel);
    setFocusSection(newSource === 'search' ? 'sidebar' : 'grid');
    setSidebarFocus(newSource === 'search' ? 'search' : 0);
    setFocusedCard(0);
    setCatScrollIdx(0);
    setGridScrollRow(0);
    setCategoryBlockMode(false);
    setShowCategorySaveModal(false);
    setParentalPasswordMode(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.instanceId]);

  useEffect(() => {
    if (!isParentalEnabled || !selectedCatId || !isCategoryBlocked(parentalType, selectedCatId)) return;
    setSelectedCatId(undefined);
    setSelectedCatName(todosLabel);
    setSidebarFocus(0);
  }, [isCategoryBlocked, isParentalEnabled, parentalType, selectedCatId, todosLabel]);

  const beginCategoryBlockMode = useCallback(() => {
    setDraftBlockedCategoryIds(new Set(
      categories
        .map((category) => String(category.category_id || ''))
        .filter((id) => isCategoryBlocked(parentalType, id))
    ));
    setCategoryBlockMode(true);
    setSearchQuery('');
    setSidebarFocus(0);
    setCatScrollIdx(0);
    setFocusSection('sidebar');
  }, [categories, isCategoryBlocked, parentalType]);

  const requestCategoryBlockMode = useCallback(() => {
    setParentalPasswordError('');
    setParentalPasswordMode(hasPassword() ? 'verify' : 'set');
  }, [hasPassword]);

  const handleParentalPasswordConfirm = useCallback((password: string) => {
    if (parentalPasswordMode === 'verify' && !verifyPassword(password)) {
      setParentalPasswordError(t('parental.wrongPassword'));
      return;
    }
    if (parentalPasswordMode === 'set') setPassword(password);
    setParentalPasswordMode(null);
    beginCategoryBlockMode();
  }, [beginCategoryBlockMode, parentalPasswordMode, setPassword, t, verifyPassword]);

  const toggleDraftCategory = useCallback((categoryId: string) => {
    setDraftBlockedCategoryIds((current) => {
      const next = new Set(current);
      if (next.has(categoryId)) next.delete(categoryId);
      else next.add(categoryId);
      return next;
    });
  }, []);

  const finishCategoryBlockMode = useCallback((save: boolean) => {
    if (save) setBlockedCategories(parentalType, Array.from(draftBlockedCategoryIds));
    setShowCategorySaveModal(false);
    setCategoryBlockMode(false);
    setSidebarFocus(0);
    setCatScrollIdx(0);
    setSelectedCatId(undefined);
    setSelectedCatName(todosLabel);
  }, [draftBlockedCategoryIds, parentalType, setBlockedCategories, todosLabel]);

  // allCats = [null (Todos), Favoritos, Bloquear Categoria, ...categories]
  const allCats = React.useMemo<(XtreamCategory | null)[]>(
    () => {
      if (source !== 'xtream') return [null, ...categories];
      const visibleCategories = categoryBlockMode
        ? categories
        : categories.filter((category) => !isParentalEnabled || !isCategoryBlocked(parentalType, String(category.category_id || '')));
      if (categoryBlockMode) return visibleCategories;
      return [
        null,
        FAVORITES_CAT,
        { category_id: BLOCK_CATEGORIES_CAT_ID, category_name: t('liveTV.blockCategories') },
        ...visibleCategories,
      ];
    },
    [categories, categoryBlockMode, FAVORITES_CAT, isCategoryBlocked, isParentalEnabled, parentalType, source, t],
  );

  // filteredAllCats — when xtream + searchQuery is active, only show cats that have matching items
  const filteredAllCats = React.useMemo<(XtreamCategory | null)[]>(() => {
    if (source !== 'xtream' || !searchQuery.trim()) return allCats;
    const q = searchQuery.trim().toLowerCase();
    return allCats.filter(cat => {
      if (cat === null) return true; // "Todos" always visible
      if (cat.category_id === FAVORITES_CAT_ID) {
        const favType = entry.type === 'movies' ? 'movies' : 'series';
        return getFavoritesByType(favType).some(f => f.name.toLowerCase().includes(q));
      }
      return content.some(c => c.categoryId === String(cat.category_id) && c.name.toLowerCase().includes(q));
    });
  }, [source, searchQuery, allCats, content, entry.type, getFavoritesByType]);

  // ── Fetch categories (xtream only) ────────────────────────────────────────────────────────
  useEffect(() => {
    // national-series / top-movies / top-series categories are populated inside the content effect (co-fetched)
    const coFetched = source === 'national-series' || source === 'top-movies' || source === 'top-series' || source === 'favorites';
    if (!authData || !hasSidebar || coFetched) { if (!coFetched) setLoadingCats(false); return; }
    let cancelled = false;
    setLoadingCats(true);
    (async () => {
      try {
        const cats = entry.type === 'movies'
          ? await getCachedMovieCategories()
          : await getCachedSeriesCategories();
        if (!cancelled) { setCategories(cats); setLoadingCats(false); }
      } catch { if (!cancelled) setLoadingCats(false); }
    })();
    return () => { cancelled = true; };
  }, [authData, entry.type, source, hasSidebar]);

  // ── Fetch content ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!authData) return;
    if (source === 'favorites') { setLoadingContent(false); return; }
    let cancelled = false;
    setLoadingContent(true);
    setContent([]);
    setFocusedCard(0);
    setGridScrollRow(0);

    (async () => {
      try {
        let cards: ListCard[] = [];

        if (isTmdbSource) {
          const CACHE_KEY = `ott_list_${source}_v4_${authData.user?.username ?? ''}`;
          const fromCache = loadListCache(CACHE_KEY);
          if (fromCache) {
            cards = fromCache;
          } else {
            const items = await getMatchedList(
              deviceId,
              activationPassword,
              source,
              getRuntimeTmdbLanguage(i18n.language),
              getRuntimeTmdbRegion(i18n.language),
              undefined,
              'full',
              consumeCatalogForceRefresh(source)
            );
            if (cancelled) return;
            cards = saveListCache(CACHE_KEY, items.map(item => ({
              id: item.streamId,
              name: item.title,
              posterUrl: item.posterUrl ?? undefined,
              streamId: item.streamId,
              tmdbId: item.tmdbId,
              backdropUrl: item.backdropUrl ?? undefined,
              categoryId: item.categoryId,
            })));
          }

          // Sidebar categories
          if (source === 'national-series' && !cancelled) {
            const allSeriesCats = await getCachedSeriesCategories();
            if (!cancelled) {
              const usedCatIds = new Set(cards.map(c => c.categoryId).filter((id): id is string => !!id));
              setCategories(allSeriesCats.filter(c => usedCatIds.has(String(c.category_id))));
              setLoadingCats(false);
            }
          } else if ((source === 'top-movies' || source === 'top-series') && !cancelled) {
            const usedYears = [...new Set(cards.map(c => c.categoryId).filter((y): y is string => !!y))]
              .sort((a, b) => Number(b) - Number(a));
            setCategories(usedYears.map(y => ({ category_id: y, category_name: y })));
            setLoadingCats(false);
          }

        } else if (source === 'search') {
          cards = [];

        } else {
          // Favorites are handled by favoritesContent memo; skip API fetch
          // Always fetch ALL items — category filtering is done in-memory
          if (selectedCatId !== FAVORITES_CAT_ID) {
            const items = entry.type === 'movies'
              ? await getCachedVodStreams()
              : await getCachedSeriesStreams();
            if (cancelled) return;
            cards = items.map(item => ({
              id: String((item as any).stream_id ?? (item as any).series_id ?? item.name),
              name: item.name,
              posterUrl: (item as any).stream_icon ?? (item as any).cover ?? undefined,
              streamId: String((item as any).stream_id ?? (item as any).series_id ?? ''),
              categoryId: String((item as any).category_id ?? '') || undefined,
              plot: item.plot || undefined,
              year: item.year || undefined,
              releaseDate: item.releaseDate || item.release_date || undefined,
            }));
          }
        }

        if (!cancelled) {
          const seen = new Set<string>();
          setContent(cards.filter(c => { if (seen.has(c.id)) return false; seen.add(c.id); return true; }));
          setLoadingContent(false);
        }
      } catch { if (!cancelled) setLoadingContent(false); }
    })();
    return () => { cancelled = true; };
  }, [authData, deviceId, activationPassword, entry.type, source, i18n.language]);

  useEffect(() => {
    if (source !== 'search') return;
    const query = searchQuery.trim();
    if (query.length < 2) {
      setContent([]);
      setLoadingContent(false);
      return;
    }
    let cancelled = false;
    setLoadingContent(true);
    const timer = window.setTimeout(() => {
      void getGlobalSearch(deviceId, activationPassword, query, consumeCatalogForceRefresh('global-search')).then((payload) => {
        if (cancelled) return;
        const cards: ListCard[] = [...payload.movies, ...payload.series, ...payload.live].map((item) => ({
          id: `${item.streamType}_${item.streamId}`,
          name: item.title,
          posterUrl: item.posterUrl || undefined,
          streamId: item.streamId,
          streamType: item.streamType,
          categoryId: item.categoryId,
          liveData: item.streamType === 'live' ? {
            stream_id: item.streamId,
            name: item.title,
            stream_icon: item.posterUrl || undefined,
            category_id: item.categoryId,
            stream_type: 'livestream',
          } : undefined,
        }));
        setContent(cards);
      }).catch(() => {
        if (!cancelled) setContent([]);
      }).finally(() => {
        if (!cancelled) setLoadingContent(false);
      });
    }, 350);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [activationPassword, deviceId, searchQuery, source]);

  // ── Favorites: load content from context (stays live) ───────────────────────
  useEffect(() => {
    if (source !== 'favorites') return;
    let cancelled = false;
    const movieFavs = getFavoritesByType('movies').filter(fav => !isKidsMode || !!fav.isKids);
    const seriesFavs = getFavoritesByType('series').filter(fav => !isKidsMode || !!fav.isKids);
    const liveFavs = isKidsMode
      ? getFavoritesByType('live').filter(fav => !!fav.isKids)
      : getFavoritesByType('live');
    void Promise.all([
      getCachedVodStreams().catch(() => []),
      getCachedSeriesStreams().catch(() => []),
    ]).then(([movies, series]) => {
      if (cancelled) return;
      const movieCategory = new Map(movies.map((item) => [String(item.stream_id || ''), String(item.category_id || '')]));
      const seriesCategory = new Map(series.map((item) => [String(item.series_id || ''), String(item.category_id || '')]));
      const movieCards: ListCard[] = movieFavs.map(fav => ({
        id: `fav_m_${fav.id}`,
        name: fav.name,
        posterUrl: fav.icon ?? undefined,
        streamId: fav.streamId ?? fav.id,
        tmdbId: parseInt(fav.id, 10) || undefined,
        streamType: 'movie' as const,
        categoryId: fav.categoryId || movieCategory.get(fav.streamId ?? fav.id) || undefined,
        isKids: fav.isKids,
      }));
      const seriesCards: ListCard[] = seriesFavs.map(fav => ({
        id: `fav_s_${fav.id}`,
        name: fav.name,
        posterUrl: fav.icon ?? undefined,
        streamId: fav.seriesId ?? fav.streamId ?? fav.id,
        streamType: 'series' as const,
        categoryId: fav.categoryId || seriesCategory.get(fav.seriesId ?? fav.streamId ?? fav.id) || undefined,
        isKids: fav.isKids,
      }));
      const liveCards: ListCard[] = liveFavs.map(fav => ({
        id: `fav_l_${fav.id}`,
        name: fav.name,
        posterUrl: fav.icon ?? undefined,
        streamId: fav.streamId ?? fav.id,
        streamType: 'live' as const,
        categoryId: fav.categoryId,
        liveData: {
          stream_id: fav.streamId ?? fav.id,
          name: fav.name,
          stream_icon: fav.icon,
          category_id: fav.categoryId,
        } as import('../../api/xtreamAPI').XtreamContent,
      }));
      setContent([...movieCards, ...seriesCards, ...liveCards]);
      setLoadingContent(false);
    }).catch(() => {
      if (!cancelled) setLoadingContent(false);
    });
    return () => { cancelled = true; };
  }, [source, isKidsMode, getFavoritesByType]);

  useEffect(() => {
    if (!isMatchedListLoading || !authData) {
      setMatchedLoadingPhase(0);
      return;
    }

    const username = authData.user?.username ?? 'unknown';
    const language = getRuntimeTmdbLanguage(i18n.language);
    const region = getRuntimeTmdbRegion(i18n.language);
    const storageKey = `ott_matched_loading_started:${source}:${language}:${region}:${username}`;
    const maxAgeMs = 10 * 60 * 1000;
    const now = Date.now();
    let startedAt = now;

    try {
      const raw = localStorage.getItem(storageKey);
      const parsed = raw ? Number(raw) : 0;
      if (Number.isFinite(parsed) && parsed > 0 && now - parsed < maxAgeMs) {
        startedAt = parsed;
      } else {
        localStorage.setItem(storageKey, String(startedAt));
      }
    } catch {
      startedAt = now;
    }

    const phaseFromElapsed = (elapsedMs: number) => {
      if (elapsedMs >= 60_000) return 3;
      if (elapsedMs >= 30_000) return 2;
      if (elapsedMs >= 10_000) return 1;
      return 0;
    };

    const updatePhase = () => setMatchedLoadingPhase(phaseFromElapsed(Date.now() - startedAt));
    updatePhase();
    const interval = window.setInterval(updatePhase, 1000);
    return () => window.clearInterval(interval);
  }, [isMatchedListLoading, authData, source, i18n.language]);

  useEffect(() => {
    if (isMatchedListLoading || !authData) return;
    const username = authData.user?.username ?? 'unknown';
    const language = getRuntimeTmdbLanguage(i18n.language);
    const region = getRuntimeTmdbRegion(i18n.language);
    try {
      localStorage.removeItem(`ott_matched_loading_started:${source}:${language}:${region}:${username}`);
    } catch {}
  }, [isMatchedListLoading, authData, source, i18n.language]);

  const matchedLoadingTitle = isMatchedListLoading
    ? t(`contentList.matchedLoading.phase${matchedLoadingPhase}.title`)
    : '';
  const matchedLoadingBody = isMatchedListLoading
    ? t(`contentList.matchedLoading.phase${matchedLoadingPhase}.body`)
    : '';

  // ── Open card ─────────────────────────────────────────────────────────────────
  const isKidsSource = source === 'kids-movies' || source === 'kids-series';

  const openCard = useCallback((item: ListCard) => {
    if (source === 'search' || source === 'favorites') {
      if (item.streamType === 'live' && item.liveData) {
        onOpenLiveChannel?.(
          item.liveData,
          content
            .filter((card) => card.streamType === 'live' && !isContentRestricted('live', card.streamId, card.categoryId))
            .map((card) => card.liveData!)
        );
        return;
      }
      if (item.streamType === 'series') {
        onOpenSeriesDetails?.({
          tmdbId: item.tmdbId ?? 0,
          title: item.name,
          posterUrl: item.posterUrl,
          backdropUrl: item.backdropUrl,
          seriesStreamId: item.streamId,
          categoryId: item.categoryId,
          isKids: item.isKids,
          plot: item.plot,
          year: item.year,
          releaseDate: item.releaseDate,
        });
        return;
      }
      // movie
      onOpenMovieDetails?.({
        tmdbId: item.tmdbId ?? 0,
        title: item.name,
        posterUrl: item.posterUrl,
        backdropUrl: item.backdropUrl,
        vodStreamId: item.streamId ?? '',
        categoryId: item.categoryId,
        isKids: item.isKids,
      });
      return;
    }
    if (entry.type === 'movies') {
      onOpenMovieDetails?.({
        tmdbId: item.tmdbId ?? 0,
        title: item.name,
        posterUrl: item.posterUrl,
        backdropUrl: item.backdropUrl,
        vodStreamId: item.streamId ?? '',
        categoryId: item.categoryId,
        isKids: isKidsSource,
      });
    } else if (entry.type === 'series') {
      onOpenSeriesDetails?.({
        tmdbId: item.tmdbId ?? 0,
        title: item.name,
        posterUrl: item.posterUrl,
        backdropUrl: item.backdropUrl,
        seriesStreamId: item.streamId,
        categoryId: item.categoryId,
        isKids: isKidsSource,
        plot: item.plot,
        year: item.year,
        releaseDate: item.releaseDate,
      });
    }
  }, [source, entry.type, content, isContentRestricted, isKidsSource, onOpenMovieDetails, onOpenSeriesDetails, onOpenLiveChannel]);

  // ── Mouse wheel scroll ────────────────────────────────────────────────────────
  const handleCatsWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const step = e.deltaY > 0 ? 1 : -1;
    setCatScrollIdx(prev => {
      const max = Math.max(0, allCats.length - CATS_VISIBLE);
      return Math.max(0, Math.min(prev + step, max));
    });
  }, [allCats.length]);
  // filteredContent — filter by Xtream category when sidebar source has a selection
  // For the favorites category, derive content from context directly so it stays live.
  const favoritesContent = useMemo((): ListCard[] => {
    if (selectedCatId !== FAVORITES_CAT_ID) return [];
    const favType = entry.type === 'movies' ? 'movies' : 'series';
    return getFavoritesByType(favType).map((fav) => ({
      id: fav.id,
      name: fav.name,
      posterUrl: fav.icon ?? undefined,
      streamId: fav.type === 'series' ? (fav.seriesId ?? fav.streamId ?? fav.id) : (fav.streamId ?? fav.id),
      tmdbId: fav.type === 'series' ? undefined : (parseInt(fav.id, 10) || undefined),
      categoryId: fav.categoryId,
      isKids: fav.isKids,
    }));
  }, [selectedCatId, entry.type, getFavoritesByType]);

  const filteredContent = useMemo(() => {
    const isVisibleCard = (card: ListCard) => {
      const type: ParentalContentType = card.streamType === 'live'
        ? 'live'
        : card.streamType === 'series'
          ? 'series'
          : card.streamType === 'movie'
            ? 'movies'
            : entry.type;
      return !isContentRestricted(type, card.streamId, card.categoryId);
    };
    const visibleContent = content.filter(isVisibleCard);
    if (source === 'search') {
      const q = searchQuery.trim().toLowerCase();
      let items = visibleContent;
      if (searchTypeFilter !== 'all') {
        items = items.filter(c => c.streamType === searchTypeFilter);
      }
      if (q) {
        items = items.filter(c => c.name.toLowerCase().includes(q));
      }
      return items;
    }
    if (source === 'favorites') {
      if (searchTypeFilter === 'all') return visibleContent;
      return visibleContent.filter(c => c.streamType === searchTypeFilter);
    }
    if (selectedCatId === FAVORITES_CAT_ID) return favoritesContent.filter(isVisibleCard);
    // For xtream: when searching, search globally then optionally filter by category
    if (source === 'xtream') {
      const q = searchQuery.trim().toLowerCase();
      if (q) {
        // Global search across all content
        const searchResult = visibleContent.filter(c => c.name.toLowerCase().includes(q));
        // Secondary filter by category if one is selected
        return selectedCatId
          ? searchResult.filter(c => c.categoryId === selectedCatId)
          : searchResult;
      }
      // No search — just category filter
      return selectedCatId ? visibleContent.filter(c => c.categoryId === selectedCatId) : visibleContent;
    }
    const sidebarFiltered = source === 'national-series' || source === 'top-movies' || source === 'top-series';
    if (!sidebarFiltered || !selectedCatId) {
      if (hasFilterBar && wideSearchQuery.trim()) {
        const q = wideSearchQuery.trim().toLowerCase();
        const filtered = visibleContent.filter(c => c.name.toLowerCase().includes(q));
        return selectedCatId ? filtered.filter(c => c.categoryId === selectedCatId) : filtered;
      }
      return visibleContent;
    }
    // sidebarFiltered + selectedCatId: apply both category and text filter
    const catFiltered = visibleContent.filter(c => c.categoryId === selectedCatId);
    if (hasFilterBar && wideSearchQuery.trim()) {
      const q = wideSearchQuery.trim().toLowerCase();
      return catFiltered.filter(c => c.name.toLowerCase().includes(q));
    }
    return catFiltered;
  }, [source, selectedCatId, content, favoritesContent, searchQuery, searchTypeFilter, hasFilterBar, wideSearchQuery, entry.type, isContentRestricted]);

  // Reset grid position when category changes
  useEffect(() => {
    setFocusedCard(0);
    setGridScrollRow(0);
  }, [selectedCatId]);
  const handleGridWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const step = e.deltaY > 0 ? 1 : -1;
    setGridScrollRow(prev => {
      const totalRows = Math.ceil(filteredContent.length / COLS);
      return Math.max(0, Math.min(prev + step, Math.max(0, totalRows - ROWS_VISIBLE)));
    });
  }, [filteredContent.length]);

  // ── Select category ───────────────────────────────────────────────────────────
  const selectCat = useCallback(
    (cat: XtreamCategory | null) => {
      if (cat === null) {
        setSelectedCatId(undefined);
        setSelectedCatName(todosLabel);
      } else {
        setSelectedCatId(cat.category_id);
        if (source === 'top-movies') {
          setSelectedCatName(t('contentList.topMoviesYear', { year: cat.category_name }));
        } else if (source === 'top-series') {
          setSelectedCatName(t('contentList.topSeriesYear', { year: cat.category_name }));
        } else {
          setSelectedCatName(cat.category_name);
        }
      }
    },
    [todosLabel, source, t],
  );

  // ── Auto-focus search input ───────────────────────────────────────────────────
  useEffect(() => {
    if ((source === 'search' || source === 'xtream') && focusSection === 'sidebar' && sidebarFocus === 'search') {
      searchInputRef.current?.focus();
    }
  }, [source, focusSection, sidebarFocus]);

  useEffect(() => {
    if (hasFilterBar && focusSection === 'wide-search') {
      wideSearchInputRef.current?.focus();
    } else if (focusSection === 'curated-hint') {
      (document.activeElement as HTMLElement)?.blur();
    }
  }, [hasFilterBar, focusSection]);

  // ── Keyboard navigation ───────────────────────────────────────────────────────
  const stateRef = useRef({
    focusSection,
    sidebarFocus,
    focusedCard,
    catScrollIdx,
    gridScrollRow,
    allCats: [] as (XtreamCategory | null)[],
    filteredAllCats: [] as (XtreamCategory | null)[],
    searchQuery: '',
    content: [] as ListCard[],
    filteredContent: [] as ListCard[],
  });

  useEffect(() => {
    stateRef.current = {
      focusSection,
      sidebarFocus,
      focusedCard,
      catScrollIdx,
      gridScrollRow,
      allCats,
      filteredAllCats,
      searchQuery,
      content,
      filteredContent,
    };
  });

  useEffect(() => {
    if (!isCuratedSource) return;
    const key = `ott_curated_hint_seen_${source}`;
    if (!localStorage.getItem(key)) {
      localStorage.setItem(key, '1');
      setCuratedModalOpen(true);
    }
  }, [source]);

  useEffect(() => {
    if (!isActive) return;

    const handler = (e: KeyboardEvent) => {
      // Don't intercept when the search input has focus — let native input handle keys
      if ((source === 'search' || source === 'xtream') && document.activeElement === searchInputRef.current) {
        if (e.key === 'Escape') {
          e.preventDefault();
          if (source === 'search') {
            onBack?.();
          } else {
            // For xtream: just blur and return focus to sidebar, don't leave the page
            searchInputRef.current?.blur();
            setFocusSection('sidebar');
            setSidebarFocus('search');
          }
        }
        if (e.key === 'GoBack' || e.key === 'BrowserBack') {
          e.preventDefault();
          // Remote back while search is focused: blur only, don't leave the page
          searchInputRef.current?.blur();
          setFocusSection('sidebar');
          setSidebarFocus('search');
        }
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          searchInputRef.current?.blur();
          setSidebarFocus(0);
          setFocusSection('sidebar');
        }
        return;
      }

      // Don't intercept when the wide search input has focus
      if (hasFilterBar && document.activeElement === wideSearchInputRef.current) {
        if (e.key === 'Escape' || e.key === 'GoBack' || e.key === 'BrowserBack') {
          e.preventDefault();
          wideSearchInputRef.current?.blur();
          setFocusSection('grid');
        }
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          wideSearchInputRef.current?.blur();
          setFocusSection('grid');
        }
        if (e.key === 'ArrowLeft' && isCuratedSource) {
          e.preventDefault();
          wideSearchInputRef.current?.blur();
          setFocusSection('curated-hint');
        }
        return;
      }

      const {
        focusSection: fs,
        sidebarFocus: sf,
        focusedCard: fc,
        catScrollIdx: csi,
        gridScrollRow: gsr,
        filteredAllCats: fac,
        searchQuery: sq,
        filteredContent: fct,
      } = stateRef.current;

      // For search/favorites source: sidebar has 4 fixed filter items (0..3)
      const catCount = (source === 'search' || source === 'favorites') ? 4 : fac.length;
      const cardCount = fct.length;
      const totalRows = Math.ceil(cardCount / COLS);

      switch (e.key) {
        // ── Back ──────────────────────────────────────────────────────────────
        case 'Escape':
        case 'GoBack':
        case 'BrowserBack':
          e.preventDefault();
          if (categoryBlockMode) {
            setShowCategorySaveModal(true);
            break;
          }
          onBack?.();
          break;

        // ── Up ────────────────────────────────────────────────────────────────
        case 'ArrowUp':
          e.preventDefault();
          if (fs === 'sidebar') {
            if (sf === 'search') return;
            if (sf === 'clear') { setSidebarFocus('search'); return; }
            if (typeof sf === 'number') {
              if (sf === 0) {
                if (source === 'xtream' && sq) {
                  setSidebarFocus('clear');
                } else if (source === 'xtream' || source === 'search') {
                  setSidebarFocus('search');
                }
                // national-series has no search box — stay at first item
              } else {
                const next = sf - 1;
                setSidebarFocus(next);
                if (next < csi) setCatScrollIdx(next);
              }
            }
          } else if (fs !== 'curated-hint') {
            // grid
            const row = Math.floor(fc / COLS);
            const col = fc % COLS;
            if (row > 0) {
              const next = (row - 1) * COLS + col;
              setFocusedCard(next);
              const nextRow = row - 1;
              if (nextRow < gsr) setGridScrollRow(nextRow);
            } else if (hasFilterBar) {
              setFocusSection('wide-search');
            }
          }
          break;

        // ── Down ──────────────────────────────────────────────────────────────
        case 'ArrowDown':
          e.preventDefault();
          if (fs === 'curated-hint') {
            setFocusSection('grid');
            setFocusedCard(0);
            setGridScrollRow(0);
          } else if (fs === 'sidebar') {
            if (sf === 'search') {
              if (source === 'xtream' && sq) {
                setSidebarFocus('clear');
              } else {
                setSidebarFocus(0);
                if (0 < csi) setCatScrollIdx(0);
              }
            } else if (sf === 'clear') {
              setSidebarFocus(0);
              if (0 < csi) setCatScrollIdx(0);
            } else if (typeof sf === 'number') {
              if (sf < catCount - 1) {
                const next = sf + 1;
                setSidebarFocus(next);
                if (next >= csi + CATS_VISIBLE) setCatScrollIdx(next - CATS_VISIBLE + 1);
              }
            }
          } else {
            // grid
            const row = Math.floor(fc / COLS);
            const col = fc % COLS;
            if (row < totalRows - 1) {
              const next = Math.min(cardCount - 1, (row + 1) * COLS + col);
              setFocusedCard(next);
              const nextRow = Math.floor(next / COLS);
              if (nextRow >= gsr + ROWS_VISIBLE) setGridScrollRow(nextRow - ROWS_VISIBLE + 1);
            }
          }
          break;

        // ── Left ──────────────────────────────────────────────────────────────
        case 'ArrowLeft':
          e.preventDefault();
          if (fs === 'grid') {
            const col = fc % COLS;
            if (col > 0) {
              setFocusedCard(fc - 1);
            } else if (!isTmdbSource || hasSidebar) {
              setFocusSection('sidebar');
            }
          }
          break;

        // ── Right ─────────────────────────────────────────────────────────────
        case 'ArrowRight':
          e.preventDefault();
          if (fs === 'curated-hint') {
            setFocusSection('wide-search');
          } else if (fs === 'sidebar') {
            if (categoryBlockMode) break;
            setFocusSection('grid');
          } else {
            const col = fc % COLS;
            if (col < COLS - 1 && fc < cardCount - 1) {
              setFocusedCard(fc + 1);
            }
          }
          break;

        // ── Enter / Select ────────────────────────────────────────────────────
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (fs === 'sidebar') {
            if (sf === 'search') {
              if (source === 'search' || source === 'xtream') {
                // Focus the actual input
                searchInputRef.current?.focus();
              }
              return;
            }
            if (sf === 'clear') {
              setSearchQuery('');
              setSidebarFocus('search');
              return;
            }
            if (typeof sf === 'number') {
              if (source === 'search' || source === 'favorites') {
                // 0=Todos, 1=Filmes, 2=Séries, 3=Canais
                const typeMap: ('all' | 'movie' | 'series' | 'live')[] = ['all', 'movie', 'series', 'live'];
                setSearchTypeFilter(typeMap[sf] ?? 'all');
                setFocusSection('grid');
                setFocusedCard(0);
                setGridScrollRow(0);
              } else {
                const cat = fac[sf];
                if (categoryBlockMode && cat) {
                  toggleDraftCategory(String(cat.category_id || ''));
                  return;
                }
                if (cat?.category_id === BLOCK_CATEGORIES_CAT_ID) {
                  requestCategoryBlockMode();
                  return;
                }
                selectCat(cat);
                setFocusSection('grid');
                setFocusedCard(0);
                setGridScrollRow(0);
              }
            }
          } else if (fs === 'curated-hint') {
            setCuratedModalOpen(true);
          } else {
            const item = fct[fc];
            if (item) openCard(item);
          }
          break;

        default:
          break;
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [categoryBlockMode, isActive, onBack, openCard, isTmdbSource, hasSidebar, requestCategoryBlockMode, selectCat, source, toggleDraftCategory]);

  // ── Derived scroll transforms ─────────────────────────────────────────────────
  const catTranslateY = -(catScrollIdx * CAT_ITEM_H_PX);
  const gridTranslateY = -(gridScrollRow * ROW_H_PX);

  // ── Virtual row window ────────────────────────────────────────────────────────
  const BUFFER_ROWS = 2;
  const totalRows = Math.ceil(filteredContent.length / COLS);
  const vStartRow = Math.max(0, gridScrollRow - BUFFER_ROWS);
  const vEndRow   = Math.min(totalRows - 1, gridScrollRow + ROWS_VISIBLE - 1 + BUFFER_ROWS);
  const rawVirtualItems = filteredContent.slice(vStartRow * COLS, (vEndRow + 1) * COLS);
  // Pad to a full row so 1fr columns stay equal width when the last row is incomplete
  const padCount = (COLS - (rawVirtualItems.length % COLS)) % COLS;
  const virtualItems: (ListCard | null)[] = [
    ...rawVirtualItems,
    ...Array.from({ length: padCount }, () => null),
  ];

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="content-list">
      {/* ── Left sidebar (categories) ─────────────────────────── */}
      {hasSidebar && <div className="content-list__sidebar">
        {/* Search input — for search and xtream sources */}
        {(source === 'search' || source === 'xtream') && (
          <div
            className={[
              'content-list__search',
              focusSection === 'sidebar' && sidebarFocus === 'search'
                ? 'content-list__search--focused'
                : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => {
              setFocusSection('sidebar');
              setSidebarFocus('search');
              setTimeout(() => searchInputRef.current?.focus(), 0);
            }}
          >
            <FiSearch size={20} />
            <input
              ref={searchInputRef}
              className="content-list__search-input"
              type="text"
              placeholder={t('contentList.search')}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                if (source === 'xtream') { setSelectedCatId(undefined); setSidebarFocus(0); }
                setFocusedCard(0);
                setGridScrollRow(0);
              }}
              onFocus={() => { setFocusSection('sidebar'); setSidebarFocus('search'); }}
              onBlur={() => {}}
            />
          </div>
        )}

        {/* Limpar Busca — shown when xtream search is active */}
        {source === 'xtream' && searchQuery && (
          <div
            className={[
              'content-list__cat-item',
              'content-list__cat-item--clear',
              focusSection === 'sidebar' && sidebarFocus === 'clear'
                ? 'content-list__cat-item--focused'
                : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => {
              setSearchQuery('');
              setFocusSection('sidebar');
              setSidebarFocus('search');
            }}
          >
            {t('contentList.clearSearch')}
          </div>
        )}

        {/* Separator */}
        <div className="content-list__separator" />

        {/* Category list */}
        <div className="content-list__cats-viewport" onWheel={handleCatsWheel}>
          {(source === 'search' || source === 'favorites') ? (
            // Fixed 4-item type filter for search / favorites
            <div className="content-list__cats-track">
              {[t('common.all'), t('contentList.movies'), t('contentList.series'), t('contentList.channels')].map((label, i) => {
                const typeMap: ('all' | 'movie' | 'series' | 'live')[] = ['all', 'movie', 'series', 'live'];
                const isActive = searchTypeFilter === typeMap[i];
                const isFocused = focusSection === 'sidebar' && sidebarFocus === i;
                return (
                  <div
                    key={label}
                    className={[
                      'content-list__cat-item',
                      isActive ? 'content-list__cat-item--active' : '',
                      isFocused ? 'content-list__cat-item--focused' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => {
                      setFocusSection('sidebar');
                      setSidebarFocus(i);
                      setSearchTypeFilter(typeMap[i]);
                      setFocusSection('grid');
                      setFocusedCard(0);
                      setGridScrollRow(0);
                    }}
                  >
                    {label}
                  </div>
                );
              })}
            </div>
          ) : loadingCats ? (
            <ShimmerCategories />
          ) : (
            <div
              className="content-list__cats-track"
              style={{ transform: `translateY(${catTranslateY}px)` }}
            >
              {filteredAllCats.map((cat, i) => {
                const label = cat?.category_name ?? t('common.all');
                const categoryId = String(cat?.category_id || '');
                const categoryBlocked = Boolean(cat && categoryId !== FAVORITES_CAT_ID && categoryId !== BLOCK_CATEGORIES_CAT_ID && (
                  categoryBlockMode ? draftBlockedCategoryIds.has(categoryId) : isCategoryBlocked(parentalType, categoryId)
                ));
                const isActive =
                  cat === null ? selectedCatId === undefined : selectedCatId === cat.category_id;
                const isFocused = focusSection === 'sidebar' && sidebarFocus === i;
                return (
                  <div
                    key={cat?.category_id ?? '__todos__'}
                    className={[
                      'content-list__cat-item',
                      isActive ? 'content-list__cat-item--active' : '',
                      isFocused ? 'content-list__cat-item--focused' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => {
                      setFocusSection('sidebar');
                      setSidebarFocus(i);
                      if (categoryBlockMode && cat) {
                        toggleDraftCategory(categoryId);
                        return;
                      }
                      if (cat?.category_id === BLOCK_CATEGORIES_CAT_ID) {
                        requestCategoryBlockMode();
                        return;
                      }
                      selectCat(cat);
                      setFocusSection('grid');
                      setFocusedCard(0);
                      setGridScrollRow(0);
                    }}
                  >
                    {categoryBlocked && <FiLock className="content-list__parental-lock" size={16} />}
                    {label}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>}

      {/* ── Right content area ──────────────────────────── */}
      <div className="content-list__content">
        {/* Top bar: breadcrumb + optional wide search */}
        <div className="content-list__topbar">
          <p className="content-list__breadcrumb">
            {entry.title}
            <span className="content-list__breadcrumb-sep">›</span>
            {selectedCatName}
          </p>
          {hasFilterBar && isCuratedSource && (
            <button
              className={[
                'content-list__curated-hint-btn',
                focusSection === 'curated-hint' ? 'content-list__curated-hint-btn--focused' : '',
              ].filter(Boolean).join(' ')}
              onClick={() => setCuratedModalOpen(true)}
            >
              <FiInfo size={14} />
              {t('contentList.curatedHintBtn')}
            </button>
          )}
          {hasFilterBar && (
            <div
              className={[
                'content-list__wide-search',
                focusSection === 'wide-search' ? 'content-list__wide-search--focused' : '',
              ].filter(Boolean).join(' ')}
              onClick={() => {
                setFocusSection('wide-search');
                setTimeout(() => wideSearchInputRef.current?.focus(), 0);
              }}
            >
              <FiSearch size={16} />
              <input
                ref={wideSearchInputRef}
                className="content-list__wide-search-input"
                type="text"
                placeholder={t('contentList.filter')}
                value={wideSearchQuery}
                onChange={(e) => {
                  setWideSearchQuery(e.target.value);
                  setFocusedCard(0);
                  setGridScrollRow(0);
                }}
                onFocus={() => setFocusSection('wide-search')}
              />
            </div>
          )}
        </div>

        {/* Grid viewport */}
        <div className="content-list__grid-viewport" onWheel={handleGridWheel}>
          {loadingContent ? (
            <>
              <div
                className={`content-list__grid${isWideGrid ? ' content-list__grid--wide' : ''}`}
                style={{ transform: `translateY(${gridTranslateY}px)` }}
              >
                {Array.from({ length: COLS * ROWS_VISIBLE }).map((_, i) => (
                  <div key={i} className="content-list__card-cell">
                    <div className="content-list__card content-list__card--loading" />
                  </div>
                ))}
              </div>
              {isMatchedListLoading && (
                <div className="content-list__loading-message">
                  <img className="content-list__loading-spinner" src={spinnerGif} alt="" />
                  <div className="content-list__loading-copy">
                    <div className="content-list__loading-title">{matchedLoadingTitle}</div>
                    <div className="content-list__loading-body">{matchedLoadingBody}</div>
                  </div>
                </div>
              )}
            </>
          ) : filteredContent.length === 0 ? (
            <div className="content-list__empty">{t('contentList.noContent')}</div>
          ) : (
            <div
              className={`content-list__grid${isWideGrid ? ' content-list__grid--wide' : ''}`}
              style={{
                transform: `translateY(${gridTranslateY}px)`,
                paddingTop: vStartRow * ROW_H_PX,
              }}
            >
              {virtualItems.map((item, localIdx) => {
                if (!item) return (
                  <div key={`pad-${localIdx}`} className="content-list__card-cell" style={{ visibility: 'hidden', pointerEvents: 'none' }} />
                );
                const globalIdx = vStartRow * COLS + localIdx;
                const isFocused = focusSection === 'grid' && focusedCard === globalIdx;
                return (
                  <div
                    key={item.id}
                    className={[
                      'content-list__card-cell',
                      isFocused ? 'content-list__card-cell--focused' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => {
                      setFocusSection('grid');
                      setFocusedCard(globalIdx);
                      openCard(item);
                    }}
                  >
                    <div
                      className="content-list__card"
                      style={item.posterUrl ? { backgroundImage: `url(${item.posterUrl})` } : {}}
                    >
                      {!item.posterUrl && (
                        <div className="content-list__card-noimg">{item.name}</div>
                      )}
                      <div className="content-list__card-title">{item.name}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {isCuratedSource && curatedModalOpen && (
        <CuratedHintModal
          title={t('contentList.curatedModalTitle')}
          body={t('contentList.curatedModalBody')}
          closeLabel={t('contentList.curatedModalClose')}
          onClose={() => setCuratedModalOpen(false)}
        />
      )}
      {parentalPasswordMode && (
        <PasswordModal
          mode={parentalPasswordMode}
          onConfirm={handleParentalPasswordConfirm}
          onCancel={() => {
            setParentalPasswordMode(null);
            setParentalPasswordError('');
          }}
          error={parentalPasswordError}
        />
      )}
      {showCategorySaveModal && (
        <ConfirmModal
          title={t('liveTV.saveBlockedCategoriesTitle')}
          description={t('liveTV.saveBlockedCategoriesDescription')}
          confirmLabel={t('common.save')}
          cancelLabel={t('common.cancel')}
          onConfirm={() => finishCategoryBlockMode(true)}
          onCancel={() => finishCategoryBlockMode(false)}
        />
      )}
    </div>
  );
};
